import { supabase } from '../lib/supabase';
import { createSlug } from './url';

interface SEOMetadata {
  title: string;
  description: string;
  keywords?: string;
}

// Varsayılan metadata
const defaultMetadata: SEOMetadata = {
  title: 'En Yakın Taksi - Taksi Durakları - Web App ',
  description: 'Türkiye\'nin en büyük taksi durağı platformu. Size en yakın taksi durağını anında bulun.',
  keywords: 'taksi, taksi durağı, en yakın taksi, taksi çağır'
};

// Türkçe karakterleri düzgün şekilde işle
function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/İ/g, 'i')
    .replace(/-/g, ' ')
    .trim();
}

// Slug'dan şehir/ilçe/mahalle adını çıkar
function extractNameFromSlug(slug: string): string {
  return slug
    .replace(/-taksi$/, '') // Sondaki -taksi'yi kaldır
    .split('-')
    .map(word => {
      // İlk harfi büyüt, Türkçe karakterleri düzgün şekilde dönüştür
      const firstChar = word.charAt(0).toLocaleUpperCase('tr-TR');
      const rest = word.slice(1).toLocaleLowerCase('tr-TR');
      return firstChar + rest;
    })
    .join(' ');
}

export async function generateSEOMetadata(pathname: string): Promise<SEOMetadata> {
  if (!pathname) return defaultMetadata;

  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return defaultMetadata;

  try {
    // Özel sayfalar için SEO ayarları
    if (segments[0] === 'taksi-ucreti-hesaplama') {
      return {
        title: 'Taksi Ücreti Hesaplama - En Yakın Taksi',
        description: 'Taksi yolculuğunuzun ücretini önceden hesaplayın. Şehir içi ve şehirler arası taksi ücret hesaplama aracı. Trafik durumuna göre gerçek zamanlı hesaplama.',
        keywords: 'taksi ücreti, taksi fiyatı, taksi hesaplama, taksi ücret hesaplama, taksi fiyat hesaplama, taksimetre hesaplama'
      };
    }

    // Şehir SEO ayarları
    if (segments[0] === 'il' && segments[1]) {
      const cityName = extractNameFromSlug(segments[1]);
      const normalizedCityName = normalizeText(cityName);
      
      // Şehri bul - case insensitive ve normalize edilmiş arama
      const { data: cities } = await supabase
        .from('cities')
        .select('*');
      
      // Şehri isim karşılaştırması yaparak bul
      const city = cities?.find(c => 
        normalizeText(c.name) === normalizedCityName
      );

      if (!city) return defaultMetadata;

      return {
        title: `${city.name} Taksi - En Yakın Taksi`,
        description: `${city.name} şehrindeki tüm taksi duraklarını görüntüleyin. Size en yakın taksi durağını anında bulun.`,
        keywords: `${city.name} taksi, ${city.name} taksi durağı, ${city.name} taksi durakları`
      };
    }

    // İlçe SEO ayarları
    if (segments[0] === 'ilce' && segments[1] && segments[2]) {
      const cityName = extractNameFromSlug(segments[1]);
      const districtName = extractNameFromSlug(segments[2]);
      
      const normalizedCityName = normalizeText(cityName);
      const normalizedDistrictName = normalizeText(districtName);

      // Şehri bul
      const { data: cities } = await supabase
        .from('cities')
        .select('*');
      
      const city = cities?.find(c => 
        normalizeText(c.name) === normalizedCityName
      );

      if (!city) return defaultMetadata;

      // İlçeyi bul
      const { data: districts } = await supabase
        .from('districts')
        .select('*')
        .eq('city_id', city.id);
      
      const district = districts?.find(d => 
        normalizeText(d.name) === normalizedDistrictName
      );

      if (!district) return defaultMetadata;

      return {
        title: `${district.name} Taksi - En Yakın Taksi`,
        description: `${district.name}, ${city.name} ilçesindeki tüm taksi duraklarını görüntüleyin. En yakın taksi durağını kolayca bulun.`,
        keywords: `${district.name} taksi, ${city.name} taksi, ${district.name} taksi durakları, ${district.name} ${city.name} taksi`
      };
    }

    // Mahalle SEO ayarları
    if (segments[0] === 'mahalle' && segments[1] && segments[2] && segments[3]) {
      const cityName = extractNameFromSlug(segments[1]);
      const districtName = extractNameFromSlug(segments[2]);
      const neighborhoodName = extractNameFromSlug(segments[3]);
      
      const normalizedCityName = normalizeText(cityName);
      const normalizedDistrictName = normalizeText(districtName);
      const normalizedNeighborhoodName = normalizeText(neighborhoodName);

      // Şehri bul
      const { data: cities } = await supabase
        .from('cities')
        .select('*');
      
      const city = cities?.find(c => 
        normalizeText(c.name) === normalizedCityName
      );

      if (!city) return defaultMetadata;

      // İlçeyi bul
      const { data: districts } = await supabase
        .from('districts')
        .select('*')
        .eq('city_id', city.id);
      
      const district = districts?.find(d => 
        normalizeText(d.name) === normalizedDistrictName
      );

      if (!district) return defaultMetadata;

      // Mahalleyi bul
      const { data: neighborhoods } = await supabase
        .from('neighborhoods')
        .select(`
          *,
          seo:neighborhood_seo(*)
        `)
        .eq('district_id', district.id);
      
      const neighborhood = neighborhoods?.find(n => 
        normalizeText(n.name) === normalizedNeighborhoodName
      );

      if (!neighborhood) return defaultMetadata;

      // Özel SEO ayarları varsa kullan
      if (neighborhood.seo?.[0]) {
        const seo = neighborhood.seo[0];
        return {
          title: seo.title,
          description: seo.description || '',
          keywords: seo.keywords
        };
      }

      // Yoksa otomatik oluştur
      return {
        title: `${neighborhood.name} Taksi - En Yakın Taksi - Web App `,
        description: `${neighborhood.name} mahallesi, ${district.name}, ${city.name} bölgesindeki en yakın taksi duraklarını görüntüleyin. Güvenli ve hızlı ulaşım için taksi durağınızı seçin.`,
        keywords: `${neighborhood.name} taksi, ${neighborhood.name} taksi durağı,  ${neighborhood.name} en yakın taksi`
      };
    }

    return defaultMetadata;
  } catch (error) {
    console.error('SEO metadata generation error:', error);
    return defaultMetadata;
  }
}

// SEO meta etiketlerini güncelle
export function updateMetaTags(metadata: SEOMetadata) {
  // Title
  document.title = metadata.title;
  
  // Description meta etiketi
  let descriptionMeta = document.querySelector('meta[name="description"]');
  if (!descriptionMeta) {
    descriptionMeta = document.createElement('meta');
    descriptionMeta.setAttribute('name', 'description');
    document.head.appendChild(descriptionMeta);
  }
  descriptionMeta.setAttribute('content', metadata.description);

  // Keywords meta etiketi
  if (metadata.keywords) {
    let keywordsMeta = document.querySelector('meta[name="keywords"]');
    if (!keywordsMeta) {
      keywordsMeta = document.createElement('meta');
      keywordsMeta.setAttribute('name', 'keywords');
      document.head.appendChild(keywordsMeta);
    }
    keywordsMeta.setAttribute('content', metadata.keywords);
  }

  // Open Graph meta etiketleri
  const ogTags = {
    'og:title': metadata.title,
    'og:description': metadata.description,
    'og:type': 'website',
    'og:locale': 'tr_TR'
  };

  Object.entries(ogTags).forEach(([property, content]) => {
    let meta = document.querySelector(`meta[property="${property}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('property', property);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  });

  // Twitter Card meta etiketleri
  const twitterTags = {
    'twitter:card': 'summary',
    'twitter:title': metadata.title,
    'twitter:description': metadata.description
  };

  Object.entries(twitterTags).forEach(([name, content]) => {
    let meta = document.querySelector(`meta[name="${name}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', name);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  });
}